import { useQuery } from '@tanstack/react-query';
import { Report, models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Button, { Spinner } from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import { PageHeading, TeamHeading } from '@/components/Layout/Parts';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import { api } from '@/utils/api';
import { RootState } from '@/utils/store';

const Wrap = styled.div`
  .powerbi-report {
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
    width: 100%;
    height: 660px;
  }
`;

const Loader = styled.div`
  margin: 100px 0;
  text-align: center;
  ${Spinner} {
    width: 30px;
    height: 30px;
  }
`;

const StyledPageHeading = styled(PageHeading)`
  text-transform: capitalize;
`;

const StyledControls = styled(Controls)`
  display: inline-flex;
  margin-right: 10px;
`;

const Header = styled.div`
  margin-bottom: 10px;
`;

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

enum Tabs {
  MANAGERS_VIEW,
  VIDEO_DATA,
  TEAM_MEMBER,
  FRESH_REUSED,
  DUMMY_GENERAL_REPORT,
}

enum WowTabs {
  FACEBOOK_PAGES_WOW,
  PARTNERS_WOW,
}

const getNavigationQueryFilter = (activeTab: Tabs) => {
  if (activeTab === Tabs.MANAGERS_VIEW || activeTab === Tabs.DUMMY_GENERAL_REPORT) {
    return "navigation/all_par eq 'All performance(earnings)'";
  }
  return "navigation/video_l eq 'Video (lifetime with earnings)'";
};

interface Props {
  network:
    | 'youtube'
    | 'facebook'
    | 'cross-platform'
    | 'research report'
    | 'market share'
    | 'testing report'
    | 'Third party report'
    | 'Third party test report'
    | 'recommendations'
    | 'top 100 fb reels'
    | 'newsflare bonus'
    | 'amazon'
    | 'employee engagement survey'
    | 'facebook competitors'
    | 'wow'
    | 'caters-media';
}

const NetworkPerformance: FC<Props> = ({ network }) => {
  const alerts = useAlerts();

  const canSeeAllReportsData = useHasPermission('view sensitive reports data');

  const productionTeams = useSelector(
    (rootState: RootState) => rootState.auth.user!.productionTeams ?? [],
  );

  const [activeTab, setActiveTab] = useState(Tabs.MANAGERS_VIEW);
  const [wowTab, setWowTab] = useState(WowTabs.FACEBOOK_PAGES_WOW);

  const [report, setReport] = useState<null | Report>(null);

  const { data, isLoading } = useQuery(
    ['powerbi_access_token'],
    () => api.get<never, { accessToken: string }>('/v1/powerbi'),
    {
      staleTime: 3 * 60 * 1000,
      retry: 1,
      onError: () => alerts.error('Failed to fetch access token for PowerBI'),
    },
  );

  const reportId = (() => {
    if (network === 'Third party report') {
      return 'ac36d178-a2ff-46d7-b53a-ae91eb7146b2';
    }

    if (network === 'Third party test report') {
      return '468ced3d-a589-447a-aab3-3b694815e092';
    }

    if (network === 'research report') {
      return '579fffb2-08f9-42fc-b7dd-8d6fc282e698';
    }

    if (network === 'testing report') {
      return '589da644-939a-48cf-b594-9400b4250f23';
    }

    if (network === 'market share') {
      return 'd1055f2e-f912-4554-8312-eb59e9f4b6a9';
    }

    if (network === 'recommendations') {
      return 'abe8fef5-3db7-4f7b-9663-08767e7db711';
    }

    if (network === 'top 100 fb reels') {
      return '5997746b-913f-4d67-b907-eb3ee6629f05';
    }

    if (network === 'newsflare bonus') {
      return 'fce8a43a-1bc1-44b5-b646-fc88e11f56f2';
    }

    if (network === 'amazon') {
      return '67d65d96-e97f-4f63-8116-23f0f1b75c10';
    }

    if (network === 'wow' && wowTab === WowTabs.FACEBOOK_PAGES_WOW) {
      return '30cd9c2f-df62-4798-9483-d143a076d6fe';
    }

    if (network === 'wow' && wowTab === WowTabs.PARTNERS_WOW) {
      return 'b8b7c542-e763-44f8-be0e-87258ac4020b';
    }

    if (network === 'caters-media') {
      return '339a4a34-994a-449f-93b9-a1eee7ac39ac';
    }

    if (network === 'employee engagement survey') {
      return '7df2f6e0-d8ec-45f7-8e20-5453ea9db928';
    }

    if (network === 'facebook competitors') {
      return '3aefe418-9234-4bc4-8b8f-c612a53e96ea';
    }

    if (network === 'youtube' && activeTab === Tabs.MANAGERS_VIEW) {
      return 'c9840136-2732-4f28-95a3-762a8cd64cc0';
    }

    if (network === 'youtube' && activeTab === Tabs.VIDEO_DATA) {
      return 'b2d5ea8b-d006-4a9b-adaf-44c728f27ba2';
    }

    if (network === 'youtube' && activeTab === Tabs.FRESH_REUSED) {
      return 'bf9b1242-1e51-42d8-91c1-0c0b31bc4cbd';
    }

    if (network === 'facebook' && activeTab === Tabs.MANAGERS_VIEW) {
      return '95675e93-8969-4f27-ac17-0e9271876ff6';
    }

    if (network === 'facebook' && activeTab === Tabs.VIDEO_DATA) {
      return '7b35d852-f102-40f6-ac01-81d9dbda7347';
    }

    if (network === 'facebook' && activeTab === Tabs.DUMMY_GENERAL_REPORT) {
      return '1739473e-6718-4a39-9103-a4fd6c19a92b';
    }

    if (network === 'cross-platform' && canSeeAllReportsData) {
      return 'ca4bbc03-6b10-47b8-bab4-fe80a18ab783';
    }

    if (network === 'cross-platform' && !canSeeAllReportsData) {
      return '411a5e41-e313-42af-b935-5e18a05ac043';
    }

    return '';
  })();

  const getEmbedUrl = () => {
    const baseUrl = 'https://app.powerbi.com/reportEmbed';

    // NEWSFLARE
    if (network === 'newsflare bonus' && canSeeAllReportsData) {
      return `${baseUrl}?filter=navigation/video_a eq 'video (activity date with earnings)'`;
    }

    if (network === 'caters-media' && canSeeAllReportsData) {
      return `${baseUrl}?filter=navigation/video_a eq 'video (activity date with earnings)'`;
    }

    // CROSS PLATFORM
    if (network === 'cross-platform') {
      return baseUrl;
    }

    // YOUTUBE
    if (network === 'youtube' && !canSeeAllReportsData) {
      if (activeTab === Tabs.VIDEO_DATA) {
        return `${baseUrl}?filter=Navigation_overview/User/user eq 'User'`;
      }
      return baseUrl;
    }

    if (network === 'youtube') {
      if (activeTab === Tabs.VIDEO_DATA) {
        return `${baseUrl}?filter=Navigation_overview/User/user eq 'Admin'`;
      }
      if (activeTab === Tabs.FRESH_REUSED) {
        return `${baseUrl}?filter=Navigation/User eq 'Admin'`;
      }
      return `${baseUrl}?filter=Navigation_overview/User eq 'Admin' and Navigation_channel_performance/User eq 'Admin'`;
    }

    // FACEBOOK
    const navigationFilter = getNavigationQueryFilter(activeTab);

    if (canSeeAllReportsData) {
      return `${baseUrl}?filter=${navigationFilter}`;
    }

    if (!productionTeams.length) {
      return baseUrl;
    }

    const teamsQueryFilter = `df_all_dist_final/team in (${productionTeams
      .map((t) => `'${t}'`)
      .join(', ')})`;

    return `${baseUrl}?filter=${teamsQueryFilter}`;
  };

  return (
    <Wrap>
      <HeadingRow>
        <div>
          <TeamHeading />
          <StyledPageHeading>
            {network}{' '}
            {network !== 'research report' &&
              network !== 'testing report' &&
              network !== 'Third party report' &&
              network !== 'Third party test report' &&
              network !== 'recommendations' &&
              network !== 'top 100 fb reels' &&
              network !== 'newsflare bonus' &&
              network !== 'employee engagement survey' &&
              'Performance'}
            <br />
          </StyledPageHeading>
        </div>
        <Button
          as="a"
          href={`https://app.powerbi.com/groups/me/reports/${reportId}`}
          target="_blank"
        >
          Open in Power BI
        </Button>
      </HeadingRow>
      {isLoading ? (
        <Loader>
          <Spinner />
        </Loader>
      ) : (
        <>
          <Header>
            {['facebook', 'youtube'].includes(network) && (
              <>
                <StyledControls>
                  <ControlsButton
                    active={activeTab === Tabs.MANAGERS_VIEW}
                    onClick={() => setActiveTab(Tabs.MANAGERS_VIEW)}
                  >
                    Manager's view
                  </ControlsButton>
                  <ControlsButton
                    active={activeTab === Tabs.VIDEO_DATA}
                    onClick={() => setActiveTab(Tabs.VIDEO_DATA)}
                  >
                    {network === 'facebook' ? 'Video data' : 'Distribution'}
                  </ControlsButton>
                  {network === 'facebook' && (
                    <ControlsButton
                      active={activeTab === Tabs.DUMMY_GENERAL_REPORT}
                      onClick={() => setActiveTab(Tabs.DUMMY_GENERAL_REPORT)}
                    >
                      Dummy general report
                    </ControlsButton>
                  )}
                  {network === 'youtube' && canSeeAllReportsData && (
                    <ControlsButton
                      active={activeTab === Tabs.FRESH_REUSED}
                      onClick={() => setActiveTab(Tabs.FRESH_REUSED)}
                    >
                      Fresh / Reused
                    </ControlsButton>
                  )}
                </StyledControls>
              </>
            )}
            {['wow'].includes(network) && (
              <>
                <StyledControls>
                  <ControlsButton
                    active={wowTab === WowTabs.FACEBOOK_PAGES_WOW}
                    onClick={() => setWowTab(WowTabs.FACEBOOK_PAGES_WOW)}
                  >
                    Facebook Pages Wow
                  </ControlsButton>
                  <ControlsButton
                    active={wowTab === WowTabs.PARTNERS_WOW}
                    onClick={() => setWowTab(WowTabs.PARTNERS_WOW)}
                  >
                    Patners Wow
                  </ControlsButton>
                </StyledControls>
              </>
            )}
            <Button $small $type="action" onClick={() => report?.fullscreen()}>
              Full screen
            </Button>
          </Header>
          <PowerBIEmbed
            key={reportId}
            embedConfig={{
              type: 'report',
              id: reportId,
              embedUrl: getEmbedUrl(),
              accessToken: data?.accessToken,
              tokenType: models.TokenType.Embed,
              settings: {
                panes: {
                  filters: {
                    visible: false,
                  },
                },
              },
            }}
            eventHandlers={
              new Map([
                [
                  'error',
                  () => {
                    alerts.error('Failed to load PowerBI Report!');
                  },
                ],
              ])
            }
            cssClassName={'powerbi-report'}
            getEmbeddedComponent={(embeddedReport) => {
              setReport(embeddedReport as Report);
            }}
          />
        </>
      )}
    </Wrap>
  );
};

export default NetworkPerformance;
